import { memo, useEffect, useRef, useState } from 'react';
import { isMobilePerformanceDevice } from '../../game/platform/mobilePerformance';
import { comboAnnouncement, comboPhase, comboTierIndex } from '../../game/runtime/comboPhase';

// CSS 側の --combo-popup-dur / --combo-break-dur と一致させること（要素の除去とアニメ終了を揃える）
const PHASE_POPUP_MS = 800;
const BREAK_FLASH_MS = 500;

// COMBO NUMBER SET（納品画像）。未納品の間はテキスト表示へフォールバックする。
const DIGITS_BASE = '/assets/ui/combo-digits';
const DIGITS_PROBE_SRC = `${DIGITS_BASE}/tier0/0.png`;

let digitsProbe: Promise<boolean> | null = null;
const probeDigits = (): Promise<boolean> => {
  digitsProbe ??= new Promise(resolve => {
    const image = new Image();
    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = DIGITS_PROBE_SRC;
  });
  return digitsProbe;
};

interface Props {
  chain: number;
  timerFraction: number;
}

interface Popup {
  readonly id: number;
  readonly name: string;
  readonly className: string;
}

/** Fix 3(b): chain/timerFraction are primitives, so memo skips work whenever a
 *  parent re-render carries the same values through. */
export const ComboHud = memo(function ComboHud({ chain, timerFraction }: Props) {
  const minimalPresentation = isMobilePerformanceDevice();
  const previousChain = useRef(chain);
  const popupId = useRef(0);
  const popupTimer = useRef(0);
  const breakTimer = useRef(0);
  const [popup, setPopup] = useState<Popup | null>(null);
  const [breaking, setBreaking] = useState(false);
  const [digitsReady, setDigitsReady] = useState(false);

  useEffect(() => {
    if (minimalPresentation) return;
    let alive = true;
    void probeDigits().then(ready => {
      if (alive && ready) setDigitsReady(true);
    });
    return () => {
      alive = false;
    };
  }, [minimalPresentation]);

  useEffect(() => () => {
    window.clearTimeout(popupTimer.current);
    window.clearTimeout(breakTimer.current);
  }, []);

  useEffect(() => {
    const previous = previousChain.current;
    previousChain.current = chain;
    if (minimalPresentation) return;
    if (chain === 0) {
      if (previous === 0) return;
      window.clearTimeout(popupTimer.current);
      setPopup(null);
      setBreaking(true);
      window.clearTimeout(breakTimer.current);
      breakTimer.current = window.setTimeout(() => setBreaking(false), BREAK_FLASH_MS);
      return;
    }
    window.clearTimeout(breakTimer.current);
    setBreaking(false);
    if (comboTierIndex(chain) <= comboTierIndex(previous)) return;
    const announcement = comboAnnouncement(chain);
    if (announcement === null) return;
    popupId.current += 1;
    setPopup({ id: popupId.current, name: announcement.name, className: announcement.className });
    window.clearTimeout(popupTimer.current);
    popupTimer.current = window.setTimeout(() => setPopup(null), PHASE_POPUP_MS);
  }, [chain, minimalPresentation]);

  if (chain === 0) {
    if (!breaking) return null;
    return (
      <div className="combo-hud" aria-label="Combo">
        <span className="combo-break">BREAK</span>
      </div>
    );
  }

  const phase = comboPhase(chain);
  const tier = comboTierIndex(chain);
  return (
    <div className={`combo-hud ${phase.className}`} aria-label="Combo">
      <span className="combo-phase">{phase.name}</span>
      {digitsReady && !minimalPresentation ? (
        <span className="combo-digits" key={chain} role="img" aria-label={String(chain)}>
          {String(chain).split('').map((digit, index) => (
            <img key={index} src={`${DIGITS_BASE}/tier${tier}/${digit}.png`} alt="" draggable={false} />
          ))}
        </span>
      ) : minimalPresentation ? (
        <strong className="combo-count">{chain}</strong>
      ) : (
        <strong className="combo-count" key={chain}>{chain}</strong>
      )}
      <span className="combo-label">CHAIN</span>
      <i className="combo-gauge"><b style={{ transform: `scaleX(${timerFraction})` }} /></i>
      {popup !== null && <span className={`combo-popup ${popup.className}`} key={popup.id}>{popup.name}!</span>}
    </div>
  );
});
